import { _ as _object_without_properties } from "@swc/helpers/_/_object_without_properties";
import { _ as _to_consumable_array } from "@swc/helpers/_/_to_consumable_array";
import { jsx as _jsx } from "react/jsx-runtime";
import { Core } from "@faire/design-tokens";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { Opacity } from "@faire/web--source/slate/Color";
import { Row } from "@faire/web--source/slate/Layout";
import React from "react";
import { useMemo } from "react";
import styled from "styled-components";
var WINDOW_TO_SHOW = [
    -2,
    -1,
    0,
    1,
    2,
    3,
    4
];
var SMALLEST_POSSIBLE_INDEX = 0;
var SIZE_OF_DOT_CONTAINER = 8;
var LARGE_SIZE_OF_DOT_CONTAINER = 18;
var DotSize;
(function(DotSize) {
    DotSize["s"] = "2px";
    DotSize["m"] = "3px";
    DotSize["l"] = "4px";
})(DotSize || (DotSize = {}));
var DotSizeLarge;
(function(DotSizeLarge) {
    DotSizeLarge["s"] = "4px";
    DotSizeLarge["m"] = "5px";
    DotSizeLarge["l"] = "6px";
})(DotSizeLarge || (DotSizeLarge = {}));
export var CarouselDots = function(_param) {
    var currentImageIndex = _param.currentImageIndex, numValidImages = _param.numValidImages, className = _param.className, colorVariant = _param.colorVariant, onClick = _param.onClick, props = _object_without_properties(_param, [
        "currentImageIndex",
        "numValidImages",
        "className",
        "colorVariant",
        "onClick"
    ]);
    var strictLocalize = useStrictLocalization().strictLocalize;
    var getDotSize = function(index) {
        var dotSizes = dotSize;
        // When we have less than we need for our scrolling window just make everything large
        if (numValidImages <= WINDOW_TO_SHOW.length) {
            return dotSizes.l;
        }
        // This block will keep the dots large when the image is all the way on one side
        // This means we will only have medium and small dots at the end of the line
        if (smallestShowingItem <= SMALLEST_POSSIBLE_INDEX) {
            if (index < WINDOW_TO_SHOW.length - 2) {
                return dotSizes.l;
            } else if (index === WINDOW_TO_SHOW.length - 2) {
                return dotSizes.m;
            }
        } else if (largestShowingItem >= largestPossibleIndex) {
            if (index > numValidImages - (WINDOW_TO_SHOW.length - 1)) {
                return dotSizes.l;
            } else if (index === numValidImages - (WINDOW_TO_SHOW.length - 1)) {
                return dotSizes.m;
            }
        }
        var _WINDOW_TO_SHOW_;
        // this.WINDOW_TO_SHOW[3] will get a value of [-1,0,1] depending on where in the window we are
        // this means centralIndex will work as the index we can consider the center
        var centralIndex = currentImageIndex + ((_WINDOW_TO_SHOW_ = WINDOW_TO_SHOW[3]) !== null && _WINDOW_TO_SHOW_ !== void 0 ? _WINDOW_TO_SHOW_ : 0);
        if (index < centralIndex - 2 || index > centralIndex + 2) {
            return dotSizes.s;
        } else if (index < centralIndex - 1 || index > centralIndex + 1) {
            return dotSizes.m;
        } else {
            return dotSizes.l;
        }
    };
    var largestPossibleIndex = numValidImages - 1;
    var smallestShowingItem = useMemo(function() {
        var _WINDOW_TO_SHOW_;
        // we want to show at all 7 dots, so if there is not enough on the right,
        //  use those on the left
        if (((_WINDOW_TO_SHOW_ = WINDOW_TO_SHOW[WINDOW_TO_SHOW.length - 1]) !== null && _WINDOW_TO_SHOW_ !== void 0 ? _WINDOW_TO_SHOW_ : 0) + currentImageIndex >= largestPossibleIndex) {
            return numValidImages - WINDOW_TO_SHOW.length;
        } else {
            var _WINDOW_TO_SHOW_1;
            return Math.max(((_WINDOW_TO_SHOW_1 = WINDOW_TO_SHOW[0]) !== null && _WINDOW_TO_SHOW_1 !== void 0 ? _WINDOW_TO_SHOW_1 : 0) + currentImageIndex, 0);
        }
    }, [
        currentImageIndex,
        largestPossibleIndex,
        numValidImages
    ]);
    var largestShowingItem = useMemo(function() {
        var _WINDOW_TO_SHOW_;
        // we want to show at all 7 dots, so if there is not enough on the left,
        //  use those on the right
        if (((_WINDOW_TO_SHOW_ = WINDOW_TO_SHOW[0]) !== null && _WINDOW_TO_SHOW_ !== void 0 ? _WINDOW_TO_SHOW_ : 0) + currentImageIndex <= SMALLEST_POSSIBLE_INDEX) {
            return WINDOW_TO_SHOW.length - 1;
        } else {
            var _WINDOW_TO_SHOW_1;
            return Math.min(((_WINDOW_TO_SHOW_1 = WINDOW_TO_SHOW[WINDOW_TO_SHOW.length - 1]) !== null && _WINDOW_TO_SHOW_1 !== void 0 ? _WINDOW_TO_SHOW_1 : 0) + currentImageIndex, numValidImages - 1);
        }
    }, [
        currentImageIndex,
        numValidImages
    ]);
    var dotContainerSize = props.dotSize === "large" ? LARGE_SIZE_OF_DOT_CONTAINER : SIZE_OF_DOT_CONTAINER;
    var dotSize = props.dotSize === "large" ? DotSizeLarge : DotSize;
    var leftOffsetPx = dotContainerSize * smallestShowingItem;
    return /*#__PURE__*/ _jsx(Wrapper, {
        className: className,
        $amount: Math.min(numValidImages, WINDOW_TO_SHOW.length),
        $dotContainerSize: dotContainerSize,
        children: /*#__PURE__*/ _jsx(StyledRow, {
            style: {
                transform: "translateX(-".concat(leftOffsetPx, "px)")
            },
            children: _to_consumable_array(new Array(numValidImages).keys()).map(function(value) {
                return /*#__PURE__*/ _jsx(DotWrapper, {
                    $dotContainerSize: dotContainerSize,
                    $height: dotSize.l,
                    children: /*#__PURE__*/ _jsx(Dot, {
                        $active: value === currentImageIndex,
                        onClick: function() {
                            onClick === null || onClick === void 0 ? void 0 : onClick(value);
                        },
                        $size: getDotSize(value),
                        $colorVariant: colorVariant,
                        // accessibility features
                        role: "button",
                        "aria-disabled": value === currentImageIndex,
                        "aria-label": strictLocalize({
                            id: "4wt7NJ",
                            defaultMessage: [
                                {
                                    type: 0,
                                    value: "Show carousel slide "
                                },
                                {
                                    type: 1,
                                    value: "slideNumber"
                                },
                                {
                                    type: 0,
                                    value: " of "
                                },
                                {
                                    type: 1,
                                    value: "totalSlidesNumber"
                                }
                            ]
                        }, {
                            slideNumber: value + 1,
                            totalSlidesNumber: numValidImages
                        })
                    })
                }, value);
            })
        })
    });
};
var Wrapper = styled.div.withConfig({
    componentId: "sc-cce892b6-0"
})([
    "width:",
    "px;margin:auto;overflow:hidden;"
], function(props) {
    return props.$dotContainerSize * props.$amount;
});
var StyledRow = styled(Row).withConfig({
    componentId: "sc-cce892b6-1"
})([
    "transition:transform 0.25s ease-in;& > *{flex-shrink:0;}"
]);
var DotWrapper = styled.div.withConfig({
    componentId: "sc-cce892b6-2"
})([
    "width:",
    "px;height:",
    ";position:relative;"
], function(param) {
    var $dotContainerSize = param.$dotContainerSize;
    return $dotContainerSize;
}, function(param) {
    var $height = param.$height;
    return $height;
});
var Dot = styled.div.withConfig({
    componentId: "sc-cce892b6-3"
})([
    "width:",
    ";height:",
    ";transition:background-color 0.25s;margin:0;position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);border-radius:50%;background-color:",
    ";"
], function(props) {
    return props.$size;
}, function(props) {
    return props.$size;
}, function(props) {
    switch(props.$colorVariant){
        case "light":
            return props.$active ? Core.surface.inverse : Core.surface.muted + Opacity.O_40;
        case "dark":
        default:
            return props.$active ? Core.surface.default : Core.surface.disabled;
    }
});
